import {
    setUsersQuery,
    useBusinessInfo,
    useUsersQuery,
} from '../../lib/Queries';
import Loading from '../Loading';
import DotDotDotMenu from '../DotDotDotMenu';
import useDelete from '../../lib/useDelete.jsx';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import {
    ArrowRightStartOnRectangleIcon,
    DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import * as Toast from '../../lib/Toast.js';
import SidePanel from '../SidePanel.jsx';
import TextInput from '../TextInput';
import { useCompleteStep } from '../../lib/Onboarding.js';
import PadlockSquare2Icon from '~/components/icons/PadlockSquare2Icon';
import UserPermissions from '~/components/settings/UserPermissions';
import { ACCESS_LIMITED } from '~/lib/usePermissions';
import InfoMessageBlock from '~/components/InfoMessageBlock.jsx';

function useDeleteInvite(id, onDeleted) {
    const { saveDelete, isDeleting } = useDelete(`/invites/${id}/delete`);

    function doDelete() {
        saveDelete().then(result => {
            setUsersQuery(result);
            if (typeof onDeleted === 'function') onDeleted();
            Toast.success('Successfully removed invite');
        });
    }

    return {
        isDeleting,
        doDelete,
    };
}

const InviteDetails = ({ invite, onClose }) => {
    const [hasCopied, setHasCopied] = useState(false);
    const { doDelete, isDeleting } = useDeleteInvite(invite.id, onClose);

    const { data } = useBusinessInfo();
    const businessName = data?.business?.name;
    const emailBody = encodeURIComponent(
        `Use this link to join me on Breely to manage ${businessName}: ${invite.url}\n\nAfter clicking the invite link you'll be able to sign up for a new account or sign into an existing Breely account to get access to this business.`,
    );

    return (
        <div>
            {data?.business?.plan === 'business' && (
                <p className="mb-4 text-gray-500">
                    You'll be billed an additional $7/month (USD) when the user
                    accepts this invite. Each invite link can only be used once.
                </p>
            )}


            <InfoMessageBlock className="mb-4">
                <p className="font-semibold mb-2">
                    Next, please share the invite link below with {invite.name}.
                </p>

                <p>
                    You can copy the link or click to email it below. They can
                    use the invite link to sign up for an account and join your
                    business.
                </p>
            </InfoMessageBlock>

            <div className="input-label">Invite Link</div>
            <input
                type="text"
                readOnly={true}
                value={invite.url}
                onFocus={e => e.target.select()}
            />

            <div className="flex items-center gap-x-4 mt-2">
                <button
                    className="btn-light-pill"
                    onClick={() => {
                        navigator.clipboard.writeText(invite.url);
                        Toast.success('Copied!');
                    }}
                >
                    Copy
                </button>

                {!!navigator.share && (
                    <button
                        className="btn-light-pill"
                        onClick={() =>
                            navigator.share({
                                url: invite.url,
                            })
                        }
                    >
                        Share Invite
                    </button>
                )}

                <a
                    className="btn-light-pill"
                    href={`mailto:?subject=Invite%20to%20${encodeURIComponent(businessName)}%20on%20Breely&body=${emailBody}`}
                >
                    Email Invite
                </a>
            </div>
            <button
                type="button"
                className="btn-pink-pill mt-6"
                onClick={doDelete}
            >
                {isDeleting ? 'Deleting...' : 'Delete Invite'}
            </button>
        </div>
    );
};

const Invite = ({ invite }) => {
    const { doDelete, isDeleting } = useDeleteInvite(invite.id);
    const [isPanelShowing, setIsPanelShowing] = useState(false);

    return (
        <div>
            <div className="flex items-center justify-between pr-4">
                <div
                    className="flex-1 py-3 cursor-pointer"
                    onClick={() => setIsPanelShowing(true)}
                >
                    {invite.name || 'Unnamed Invite'}
                </div>
                <DotDotDotMenu
                    options={[
                        {
                            name: 'Copy Invite Link',
                            icon: <DocumentDuplicateIcon />,
                            onClick: () => {
                                navigator.clipboard
                                    .writeText(invite.url)
                                    .then(() => {
                                        Toast.success('Copied!');
                                    })
                                    .catch(() => {
                                        Toast.error(
                                            'Failed to copy to clipboard',
                                        );
                                    });
                                return true;
                            },
                        },
                        {
                            name: isDeleting ? 'Deleting...' : 'Delete',
                            isDelete: true,
                            onClick: doDelete,
                        },
                    ]}
                />
            </div>
            <SidePanel
                isOpen={isPanelShowing}
                onClose={() => setIsPanelShowing(false)}
                title="Invite User"
            >
                <InviteDetails
                    invite={invite}
                    onClose={() => setIsPanelShowing(false)}
                />
            </SidePanel>
        </div>
    );
};

const NewInvitePanel = ({ isOpen, onClose }) => {
    const [name, setName] = useState('');
    const [permissions, setPermissions] = useState([]);
    const [invite, setInvite] = useState(null);
    const { save, isSaving } = useBusinessPostApi(`/invites`);
    const { complete } = useCompleteStep();
    const { data } = useBusinessInfo();

    useEffect(() => {
        if (isOpen) {
            setName('');
            setInvite(null);
        }
    }, [isOpen]);

    function saveInvite() {
        save({
            name: name,
            permissions: permissions,
        }).then(result => {
            setUsersQuery(result.users);
            setInvite(result.invite);
            complete('settings-users');
        });
    }

    return (
        <SidePanel isOpen={isOpen} onClose={onClose} title="Invite User">
            {!invite && (
                <form
                    onSubmit={ev => {
                        ev.preventDefault();
                        saveInvite();
                    }}
                >
                    <TextInput
                        value={name}
                        onChange={setName}
                        required={true}
                        label="Name"
                        autoFocus={true}
                    />

                    <div className="panel-label mt-4">Access Level</div>
                    <UserPermissions
                        value={permissions}
                        setValue={setPermissions}
                    />

                    <button className="btn-save mt-4">
                        {isSaving ? 'Generating Invite...' : 'Generate Invite'}
                    </button>

                    {data?.business?.plan === 'business' && (
                        <p className="mt-4 text-gray-500 text-sm">
                            You'll be billed an additional $7/month (USD) when the user
                            accepts this invite.
                        </p>
                    )}
                </form>
            )}
            {!!invite && <InviteDetails invite={invite} onClose={onClose} />}
        </SidePanel>
    );
};

const Invites = ({ invites }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="mb-6">
            <div className="panel-label">User Invites (Pending)</div>
            <div className="panel px-4 pr-0 mb-4 divide-y">
                {!invites?.length && (
                    <div className="text-gray-500 my-1 py-3">
                        You have no pending user invites
                    </div>
                )}
                {!!invites &&
                    invites.map(invite => (
                        <Invite key={invite.id} invite={invite} />
                    ))}
            </div>
            <button className="btn-light-pill" onClick={() => setIsOpen(true)}>
                Invite New User
            </button>
            <NewInvitePanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
        </div>
    );
};

const UserRow = ({ userData, iAmOwner }) => {
    const { user, canDelete, isMe } = userData;

    const [isEditingPermissions, setIsEditingPermissions] = useState(false);
    const [permissions, setPermissions] = useState(user.pivot.permissions);

    const { isDeleting, saveDelete } = useDelete(`/users/${user.id}/remove`, {
        prompt: 'Are you sure you want to remove this user from this business?',
    });

    const { save: saveTransfer, isSaving: isTransferring } = useBusinessPostApi(
        `/users/${user.id}/make-owner`,
    );

    const { save: savePermissions, isSaving: isSavingPermissions } =
        useBusinessPostApi(`/users/${user.id}/permissions`);

    useEffect(() => {
        if (isEditingPermissions) {
            setPermissions(user.pivot.permissions);
        }
    }, [isEditingPermissions]);

    function doSavePermissions() {
        savePermissions({
            permissions: permissions,
        }).then(result => {
            Toast.success('Successfully saved permissions');
            setUsersQuery(result);
        });
    }

    function doDelete() {
        saveDelete().then(result => {
            setUsersQuery(result);
        });
    }

    function doTransferOwnership() {
        if (
            prompt(
                `Are you sure you want to transfer account ownership to ${user?.email}?\nThis will remove you as the owner.\n\nType CONFIRM in all caps to confirm.`,
            ) === 'CONFIRM'
        ) {
            saveTransfer().then(result => {
                Toast.success('Successfully transferred account ownership');
                setUsersQuery(result);
            });
        }
    }

    const menuOptions = [];
    if (iAmOwner && !user.pivot.permissions?.owner) {
        menuOptions.push({
            name: 'Edit Permissions',
            icon: <PadlockSquare2Icon />,
            onClick: () => {
                setIsEditingPermissions(true);
                return true;
            },
        });
        menuOptions.push({
            name: isTransferring ? 'Making Owner...' : 'Make Owner',
            icon: <ArrowRightStartOnRectangleIcon />,
            onClick: doTransferOwnership,
        });
    }

    if (canDelete) {
        menuOptions.push({
            name: 'Delete',
            isDelete: true,
            onClick: doDelete,
        });
    }

    const isOwner = !!user.pivot.permissions?.owner;
    const isLimited = user.pivot.permissions?.role === ACCESS_LIMITED;
    const isFull = !isLimited;

    return (
        <div>
            <div className="flex items-center justify-between py-3 pr-4">
                <div>
                    {user?.email || user?.auth0_id}
                </div>
                <div className="flex items-center">
                    {!!isMe && <span className="tag bg-gray-500 ml-2">me</span>}
                    {isOwner && (
                        <span className="tag bg-green-300 ml-2">owner</span>
                    )}
                    {!isOwner && isLimited && (
                        <span className="tag bg-yellow-100 !text-yellow-600 ml-2">
                            limited access
                        </span>
                    )}
                    {!isOwner && isFull && (
                        <span className="tag bg-green-100 !text-green-700 ml-2">
                            full access
                        </span>
                    )}
                    {menuOptions.length === 0 && (
                        <div className="w-[32px]">&nbsp;</div>
                    )}
                    {menuOptions.length > 0 && (
                        <DotDotDotMenu
                            btnClassName="ml-2"
                            options={menuOptions}
                        />
                    )}
                </div>
            </div>
            <SidePanel
                isOpen={isEditingPermissions}
                onClose={() => setIsEditingPermissions(false)}
                title="Permissions"
                onCancel={() => setIsEditingPermissions(false)}
                showSave={true}
                saveLabel={isSavingPermissions ? 'Saving...' : 'Save'}
                onSave={doSavePermissions}
            >
                <h2>{user.email}</h2>
                <UserPermissions
                    value={permissions}
                    setValue={setPermissions}
                    className="mt-4"
                />
            </SidePanel>
        </div>
    );
};

const UsersSettings = ({}) => {
    const { data, isLoading } = useUsersQuery();

    const { users, invites, i_am_owner, billable } = data || {};

    return (
        <div>
            <Invites invites={invites} />

            <div className="panel-label">
                Users (
                {users?.length !== billable ? `${users?.length} users, ` : ''}
                {billable} billable user{billable > 1 ? 's' : ''})
            </div>
            <div className="panel px-4 pr-0 divide-y">
                {!!users &&
                    users.map(userData => (
                        <UserRow
                            key={userData.user?.id}
                            userData={userData}
                            iAmOwner={i_am_owner}
                        />
                    ))}
                {isLoading && <Loading className="py-4" />}
            </div>
        </div>
    );
};

export default UsersSettings;
